import React, { useState, useEffect, useCallback } from 'react';
import { GitCompareArrows, CheckCircle, XCircle, Link2, RefreshCw, ArrowRight } from 'lucide-react';
import { bankAPI } from '../../lib/api';
import { useTenant } from '../../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

function fmt(v) {
    const n = Number(v || 0);
    return n.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ConfBadge({ confidence, status }) {
    if (status === 'confirmed') return <Badge variant="success">Confirmed</Badge>;
    const pct = Math.round((confidence || 0) * 100);
    return <Badge variant="secondary">Suggested · {pct}%</Badge>;
}

export default function BankMatchingPage() {
    const { currentTenant } = useTenant();
    const [fyOptions, setFyOptions] = useState([]);
    const [fy, setFy] = useState(null);
    const [buckets, setBuckets] = useState(null);
    const [coding, setCoding] = useState(null); // P4: receipt-only coding (mellemregning + confirm)
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null); // bank_line_id / receipt_id currently acting on
    const [relinkFor, setRelinkFor] = useState(null); // bank_line_id being relinked

    // Discover available fiscal years from the stored bank lines.
    useEffect(() => {
        if (!currentTenant) return;
        bankAPI.getLines(currentTenant.id)
            .then((r) => {
                const years = r.data.fiscal_years || [];
                setFyOptions(years);
                setFy((prev) => prev || (years.length ? String(years[years.length - 1]) : null));
            })
            .catch(() => {});
    }, [currentTenant]);

    const load = useCallback(async () => {
        if (!currentTenant || !fy) { setBuckets(null); return; }
        setLoading(true);
        try {
            const [m, c] = await Promise.all([
                bankAPI.getMatches(currentTenant.id, fy),
                bankAPI.getCoding(currentTenant.id, fy).catch(() => null),
            ]);
            setBuckets(m.data);
            setCoding(c?.data || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not load matches');
        } finally { setLoading(false); }
    }, [currentTenant, fy]);

    const confirmPrivate = async (receiptId, confirmed) => {
        setBusy(receiptId);
        try {
            const r = await bankAPI.confirmPrivate(currentTenant.id, receiptId, confirmed);
            setCoding(r.data);
            toast.success(confirmed ? 'Confirmed — books to mellemregning' : 'Confirmation removed');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Action failed');
        } finally { setBusy(null); }
    };

    useEffect(() => { load(); }, [load]);

    const act = async (fn, bankLineId, okMsg) => {
        setBusy(bankLineId);
        try {
            const r = await fn();
            setBuckets(r.data);
            setRelinkFor(null);
            if (okMsg) toast.success(okMsg);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Action failed');
        } finally { setBusy(null); }
    };

    const confirm = (lineId, receiptId = null) =>
        act(() => bankAPI.confirmMatch(currentTenant.id, lineId, receiptId), lineId, 'Match confirmed');
    const reject = (lineId) =>
        act(() => bankAPI.rejectMatch(currentTenant.id, lineId), lineId, 'Match rejected');

    if (!currentTenant) {
        return (
            <div className="space-y-6" data-testid="bank-match-page">
                <h1 className="font-heading text-3xl font-bold">Bank matching</h1>
                <Card><CardContent className="py-12 text-center text-muted-foreground">Select a company to review matching.</CardContent></Card>
            </div>
        );
    }

    const s = buckets?.summary;
    const unmatchedReceipts = buckets?.receipt_only || [];

    return (
        <div className="space-y-8" data-testid="bank-match-page">
            <div className="flex items-start justify-between">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-2">
                        <GitCompareArrows className="h-7 w-7 text-primary" /> Bank matching
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {currentTenant.name} — match bank lines to receipts. The match decides the contra later; here we only confirm matching.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={fy || ''} onValueChange={setFy}>
                        <SelectTrigger className="h-9 w-36" data-testid="match-fy"><SelectValue placeholder="Fiscal year" /></SelectTrigger>
                        <SelectContent>
                            {fyOptions.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
                        </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={load} title="Recompute" data-testid="match-refresh">
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                </div>
            </div>

            {!fy ? (
                <Card><CardContent className="py-12 text-center text-muted-foreground">
                    No bank lines yet — import a statement on the Bank statements screen first.
                </CardContent></Card>
            ) : (
                <>
                    {s && (
                        <div className="flex gap-3 text-sm">
                            <Badge variant="success">Matched {s.matched}</Badge>
                            <Badge variant="secondary">Bank-only {s.bank_only}</Badge>
                            <Badge variant="outline">Receipt-only {s.receipt_only}</Badge>
                            <span className="text-muted-foreground self-center">auto-accept ≥ {Math.round((s.auto_accept_threshold || 0) * 100)}%</span>
                        </div>
                    )}

                    {/* MATCHED */}
                    <Card>
                        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><CheckCircle className="h-5 w-5 text-success" /> Matched pairs ({buckets?.matched?.length || 0})</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            {(!buckets?.matched || buckets.matched.length === 0) ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">No matches yet.</p>
                            ) : buckets.matched.map((mt) => (
                                <div key={mt.bank_line.id} className="flex items-center gap-3 rounded-md border p-3" data-testid={`matched-${mt.bank_line.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{mt.bank_line.text || '—'}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{mt.bank_line.date} · {fmt(mt.bank_line.amount_signed)}</div>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm truncate">{mt.receipt.supplier || mt.receipt.filename || '—'}</div>
                                        <div className="text-xs text-muted-foreground font-mono">{mt.receipt.invoice_date} · {fmt(mt.receipt.total)}{mt.cross_currency ? ' · cross-currency' : ''}</div>
                                    </div>
                                    <ConfBadge confidence={mt.confidence} status={mt.status} />
                                    <div className="flex items-center gap-1 shrink-0">
                                        {mt.status !== 'confirmed' && (
                                            <Button size="sm" variant="outline" disabled={busy === mt.bank_line.id}
                                                    onClick={() => confirm(mt.bank_line.id)} data-testid={`accept-${mt.bank_line.id}`}>
                                                <CheckCircle className="h-4 w-4 mr-1" /> Accept
                                            </Button>
                                        )}
                                        <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10"
                                                disabled={busy === mt.bank_line.id} onClick={() => reject(mt.bank_line.id)} data-testid={`reject-${mt.bank_line.id}`}>
                                            <XCircle className="h-4 w-4 mr-1" /> Reject
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    {/* BANK-ONLY */}
                    <Card>
                        <CardHeader><CardTitle className="text-lg">Unmatched bank lines ({buckets?.bank_only?.length || 0})</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            {(!buckets?.bank_only || buckets.bank_only.length === 0) ? (
                                <p className="text-sm text-muted-foreground py-4 text-center">None — every bank line is matched.</p>
                            ) : buckets.bank_only.map((b) => (
                                <div key={b.bank_line.id} className="rounded-md border p-3" data-testid={`bankonly-${b.bank_line.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{b.bank_line.text || '—'}</div>
                                            <div className="text-xs text-muted-foreground font-mono">{b.bank_line.date} · {fmt(b.bank_line.amount_signed)}</div>
                                        </div>
                                        {b.hint && (
                                            <div className="text-xs text-amber-600 max-w-[40%] truncate">
                                                Possible: {b.hint.supplier} ({Math.round((b.hint.confidence || 0) * 100)}%)
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1 shrink-0">
                                            {b.hint && (
                                                <Button size="sm" variant="outline" disabled={busy === b.bank_line.id}
                                                        onClick={() => confirm(b.bank_line.id, b.hint.receipt_id)} data-testid={`accepthint-${b.bank_line.id}`}>
                                                    Accept suggestion
                                                </Button>
                                            )}
                                            <Button size="sm" variant="ghost" disabled={busy === b.bank_line.id}
                                                    onClick={() => setRelinkFor(relinkFor === b.bank_line.id ? null : b.bank_line.id)} data-testid={`relink-${b.bank_line.id}`}>
                                                <Link2 className="h-4 w-4 mr-1" /> Relink
                                            </Button>
                                        </div>
                                    </div>
                                    {relinkFor === b.bank_line.id && (
                                        <div className="mt-2 flex items-center gap-2">
                                            <Select onValueChange={(rid) => confirm(b.bank_line.id, rid)}>
                                                <SelectTrigger className="h-8" data-testid={`relink-select-${b.bank_line.id}`}>
                                                    <SelectValue placeholder="Choose a receipt to link…" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {unmatchedReceipts.length === 0 && <SelectItem value="__none__" disabled>No unmatched receipts</SelectItem>}
                                                    {unmatchedReceipts.map((r) => (
                                                        <SelectItem key={r.receipt.id} value={r.receipt.id}>
                                                            {r.receipt.supplier || r.receipt.filename} · {fmt(r.receipt.total)} · {r.receipt.invoice_date}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    {/* RECEIPT-ONLY — privately paid -> mellemregning (P4 contra + confirm) */}
                    <Card>
                        <CardHeader><CardTitle className="text-lg">Unmatched receipts — privately paid ({unmatchedReceipts.length})</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            <p className="text-xs text-muted-foreground">Receipts with no bank line — paid by the owner. Confirm each to book the expense against <span className="font-semibold">mellemregning</span> (the company owes the owner back).</p>
                            {(() => {
                                const ro = (coding?.receipt_only && coding.receipt_only.length)
                                    ? coding.receipt_only
                                    : unmatchedReceipts.map((r) => ({ receipt: r.receipt }));
                                if (ro.length === 0) return <p className="text-sm text-muted-foreground py-4 text-center">None.</p>;
                                return ro.map((r) => (
                                    <div key={r.receipt.id} className="rounded-md border p-3 space-y-2" data-testid={`receiptonly-${r.receipt.id}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm truncate">{r.receipt.supplier || r.receipt.filename || '—'}</div>
                                                <div className="text-xs text-muted-foreground font-mono">{r.receipt.invoice_date} · {fmt(r.receipt.total)} {r.receipt.currency || ''}</div>
                                            </div>
                                            {r.privately_paid_confirmed
                                                ? <Badge variant="success">Confirmed → mellemregning</Badge>
                                                : <Badge variant="outline">Awaiting confirm</Badge>}
                                        </div>
                                        {r.coding && (
                                            <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1">
                                                <span>Debit: <span className="font-mono font-semibold">{r.coding.account.code || '—'}</span> {r.coding.account.name}</span>
                                                <span>VAT: <span className="font-mono font-semibold">{r.coding.vat.code || '—'}</span></span>
                                                <span className={r.contra?.flagged ? 'text-amber-700' : ''}>
                                                    Credit (contra): <span className="font-mono font-semibold">{r.contra?.code || '—'}</span> {r.contra?.name}
                                                    {r.contra?.flagged ? ` · ${r.contra?.note}` : ''}
                                                </span>
                                            </div>
                                        )}
                                        <div>
                                            {r.privately_paid_confirmed ? (
                                                <Button size="sm" variant="ghost" disabled={busy === r.receipt.id} onClick={() => confirmPrivate(r.receipt.id, false)} data-testid={`unconfirm-${r.receipt.id}`}>Undo</Button>
                                            ) : (
                                                <Button size="sm" variant="outline" disabled={busy === r.receipt.id} onClick={() => confirmPrivate(r.receipt.id, true)} data-testid={`confirmprivate-${r.receipt.id}`}>
                                                    <CheckCircle className="h-4 w-4 mr-1" /> Privately paid → mellemregning
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                ));
                            })()}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
